import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import RichTextEditor from "@/components/RichTextEditor";
import ItemAssigner from "@/components/ItemAssigner";
import DeleteConfirmDialog from "@/components/DeleteConfirmDialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  ArrowLeft,
  Save,
  Loader2,
  Upload,
  X,
  Trash2,
  Plus,
  Image as ImageIcon,
  Globe,
  Search,
  ChevronUp,
  ChevronDown,
  Puzzle,
} from "lucide-react";
import { toast } from "@/lib/toastBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function LearningExperienceForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const fileRef = useRef(null);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    photo_url: "",
    patient_scenario: "",
    tasks: "",
    status: "draft",
    embed_enabled: false,
    embed_url: "",
    embed_title: "",
    scoring_config: {
      method: "native_only",
      weight_native: 1.0,
      weight_external: 0.0,
      external_max: 100,
    },
    cfi_mode: false,
    cfi_confidence_likert_id: null,
  });

  // Local question state for new (unsaved) LEXPs
  const [pendingQuestions, setPendingQuestions] = useState([]);
  const [showQuestionPicker, setShowQuestionPicker] = useState(false);
  const [allQuestions, setAllQuestions] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerSearch, setPickerSearch] = useState("");
  const [pickerTypeFilter, setPickerTypeFilter] = useState("all");

  const fetchLE = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/learning-experiences/${id}`, {
        withCredentials: true,
      });
      setForm({
        title: data.title || "",
        description: data.description || "",
        photo_url: data.photo_url || "",
        patient_scenario: data.patient_scenario || "",
        tasks: data.tasks || "",
        status: data.status || "draft",
        embed_enabled: data.embed_enabled || false,
        embed_url: data.embed_url || "",
        embed_title: data.embed_title || "",
        scoring_config: data.scoring_config || {
          method: "native_only",
          weight_native: 1.0,
          weight_external: 0.0,
          external_max: 100,
        },
        cfi_mode: !!data.cfi_mode,
        cfi_confidence_likert_id: data.cfi_confidence_likert_id || null,
      });
    } catch {
      toast.error("Failed to load");
      navigate("/admin/learning-experiences");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (isEdit) fetchLE();
  }, [isEdit, fetchLE]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/upload`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((prev) => ({ ...prev, photo_url: data.url }));
      toast.success("Photo uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      if (isEdit) {
        await axios.put(`${API}/learning-experiences/${id}`, form, {
          withCredentials: true,
        });
        toast.success("Updated");
      } else {
        const { data } = await axios.post(`${API}/learning-experiences`, form, {
          withCredentials: true,
        });
        // Assign pending questions to the newly created LEXP
        if (pendingQuestions.length > 0) {
          for (const q of pendingQuestions) {
            await axios.post(
              `${API}/learning-experiences/${data.id}/items`,
              { item_id: q.id },
              { withCredentials: true }
            ).catch(() => {});
          }
        }
        toast.success("Created");
        navigate(`/admin/learning-experiences/${data.id}`, { replace: true });
      }
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  // ─── Local question picker for new (unsaved) LEXPs ───
  const TYPE_LABELS = { mc: "MC", sata: "SATA", task: "Task", likert: "Likert", cfi_mc: "CFI MC", external_score: "Ext Score" };
  const TYPE_COLORS = {
    mc: "bg-blue-500/15 text-blue-400 border-blue-500/20",
    sata: "bg-purple-500/15 text-purple-400 border-purple-500/20",
    task: "bg-amber-500/15 text-amber-400 border-amber-500/20",
    likert: "bg-teal-500/15 text-teal-400 border-teal-500/20",
    cfi_mc: "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30",
  };
  const CAT_COLORS = {
    survey: "bg-cyan-500/15 text-cyan-400 border-cyan-500/20",
    assessment: "bg-white/5 text-[#A1A9B8] border-white/10",
  };

  const openQuestionPicker = async () => {
    setShowQuestionPicker(true);
    setPickerSearch("");
    setPickerTypeFilter("all");
    setPickerLoading(true);
    try {
      const { data } = await axios.get(`${API}/items`, { withCredentials: true });
      setAllQuestions(data.filter((i) => i.status === "published"));
    } catch {
      toast.error("Failed to load questions");
    } finally {
      setPickerLoading(false);
    }
  };

  const addPendingQuestion = (item) => {
    if (pendingQuestions.some((q) => q.id === item.id)) return;
    setPendingQuestions((prev) => [...prev, item]);
  };

  const removePendingQuestion = (itemId) => {
    setPendingQuestions((prev) => prev.filter((q) => q.id !== itemId));
  };

  const movePendingQuestion = (idx, direction) => {
    const list = [...pendingQuestions];
    const targetIdx = direction === "up" ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= list.length) return;
    [list[idx], list[targetIdx]] = [list[targetIdx], list[idx]];
    setPendingQuestions(list);
  };

  const pendingIds = new Set(pendingQuestions.map((q) => q.id));
  // CFI mode: cap is 1 CFI MC. The system Likert is auto-attached server-side.
  const pendingCfiMcCount = pendingQuestions.filter((q) => q.item_type === "cfi_mc").length;
  const filteredPickerQuestions = allQuestions.filter((i) => {
    // External Score items cannot be added to LEXPs
    if (i.item_type === "external_score") return false;
    if (form.cfi_mode) {
      // CFI mode: only CFI MC items can be picked. Hide once cap of 1 is reached.
      if (i.item_type !== "cfi_mc") return false;
      if (pendingCfiMcCount >= 1 && !pendingIds.has(i.id)) return false;
    } else {
      // Non-CFI LEXP: CFI MC items must not appear (server enforces this too).
      if (i.item_type === "cfi_mc") return false;
    }
    if (pickerTypeFilter !== "all" && i.item_type !== pickerTypeFilter) return false;
    if (pickerSearch.trim()) {
      const q = pickerSearch.toLowerCase();
      const stem = (i.stem || "").replace(/<[^>]*>/g, "").toLowerCase();
      // topic_tag may be a string, an array of tag ids, or missing — normalize before matching
      const tagStr = Array.isArray(i.topic_tag)
        ? i.topic_tag.join(" ").toLowerCase()
        : String(i.topic_tag || "").toLowerCase();
      if (!(i.title || "").toLowerCase().includes(q) && !stem.includes(q) && !tagStr.includes(q)) return false;
    }
    return true;
  });


  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/learning-experiences/${id}`, {
        withCredentials: true,
      });
      toast.success("Deleted");
      navigate("/admin/learning-experiences");
    } catch {
      toast.error("Delete failed");
      throw new Error("delete failed");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin" style={{ color: "var(--primary-color, #4A90D9)" }} />
      </div>
    );
  }

  const photoSrc = form.photo_url
    ? form.photo_url.startsWith("/")
      ? `${BACKEND}${form.photo_url}`
      : form.photo_url
    : null;

  return (
    <div className="max-w-3xl space-y-6" data-testid="le-form-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/admin/learning-experiences")}
          data-testid="le-back-btn"
          className="text-[#A1A9B8] hover:text-white hover:bg-white/5 w-fit"
        >
          <ArrowLeft className="h-4 w-4 mr-1.5" />
          Back to Library
        </Button>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap justify-end">
          <button
            type="button"
            onClick={() => setForm((p) => ({ ...p, status: p.status === "draft" ? "published" : "draft" }))}
            data-testid="le-status-toggle"
          >
            <Badge
              className={
                form.status === "published"
                  ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/20 cursor-pointer hover:bg-emerald-500/25"
                  : "bg-amber-500/15 text-amber-400 border-amber-500/20 cursor-pointer hover:bg-amber-500/25"
              }
            >
              {form.status}
            </Badge>
          </button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={saving || !form.title.trim()}
            data-testid="le-save-btn"
            className="h-9 px-4 sm:px-5 font-body font-medium text-white rounded-lg hover:opacity-90 shrink-0"
            style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
          >
            {saving ? (
              <><Loader2 className="h-4 w-4 animate-spin sm:mr-1.5" /><span className="hidden sm:inline">Saving...</span></>
            ) : (
              <><Save className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">{isEdit ? "Save Changes" : "Create"}</span><span className="sm:hidden">{isEdit ? "Save" : "Create"}</span></>
            )}
          </Button>
          {isEdit && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDeleteOpen(true)}
              disabled={deleting}
              data-testid="le-delete-btn"
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      <DeleteConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        tier={2}
        entityName={form?.title}
        entityType="lexp"
        entityId={id}
        onConfirm={handleDelete}
      />

      <h2 className="font-heading text-xl sm:text-2xl font-bold text-white tracking-tight break-words" data-testid="le-form-title">
        {isEdit ? "Edit Learning Experience" : "Create Learning Experience"}
      </h2>

      <form onSubmit={handleSave} className="space-y-6">
        {/* Title + Status */}
        <div className="bg-[#11141A] border border-white/10 rounded-xl p-4 sm:p-6 space-y-5">
          <h3 className="font-body text-base font-bold text-white">Details</h3>

          <div className="space-y-1.5">
            <Label className="text-[#A1A9B8] font-body text-sm">Title <span className="text-red-400">*</span></Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              required
              data-testid="le-title-input"
              className="bg-[#0A0C10] border-white/10 text-white focus:border-[var(--primary-color,#4A90D9)] h-10"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-[#A1A9B8] font-body text-sm">Description</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Brief overview of this learning experience"
              rows={3}
              data-testid="le-description-input"
              className="bg-[#0A0C10] border-white/10 text-white placeholder:text-[#6E7787] focus:border-[var(--primary-color,#4A90D9)] resize-none"
            />
          </div>

          {/* Photo Upload */}
          <div className="space-y-1.5">
            <Label className="text-[#A1A9B8] font-body text-sm">Photo</Label>
            {photoSrc ? (
              <div className="relative w-full max-w-xs">
                <img src={photoSrc} alt="Preview" className="rounded-lg border border-white/10 w-full h-40 object-cover" />
                <button
                  type="button"
                  onClick={() => setForm((p) => ({ ...p, photo_url: "" }))}
                  className="absolute top-2 right-2 h-6 w-6 rounded-full bg-black/60 flex items-center justify-center text-white hover:bg-black/80"
                  data-testid="le-photo-remove"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <input ref={fileRef} type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" id="photo-upload" data-testid="le-photo-input" />
                <label
                  htmlFor="photo-upload"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-dashed border-white/10 text-sm text-[#A1A9B8] font-body cursor-pointer hover:border-white/20 hover:text-white transition-all"
                >
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
                  {uploading ? "Uploading..." : "Upload photo"}
                </label>
              </div>
            )}
          </div>
        </div>

        {/* MIND CFI Mode toggle. When ON, this LEXP becomes a Confidence-
            Fluency Instrument deployment: only one CFI MC item is allowed,
            plus an auto-attached singleton system Likert (1–5 confidence
            scale, fixed labels). The toggle is locked once non-CFI items
            are attached — the admin must remove them first (server enforces
            the same guard).  Read more: scope: MIND CFI Phase 1. */}
        <div className="bg-[#11141A] border border-white/10 rounded-xl p-4 sm:p-6 space-y-3" data-testid="le-cfi-section" style={{ borderLeftWidth: 4, borderLeftColor: "#D946EF" }}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <h3 className="font-body text-base font-bold text-white flex items-center gap-2">
                MIND CFI MODE
                {form.cfi_mode && (
                  <Badge className="bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-500/40 text-xs">Active</Badge>
                )}
              </h3>
              <p className="text-xs text-[#A1A9B8] font-body mt-1 leading-relaxed">
                Enables Confidence-Fluency Instrument configuration. Please note, this configuration utilizes the survey item of a Confidence Scale (Likert) and limits items to only CFI Multiple Choice Items (1 item per LEXP).
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                if (!form.cfi_mode) {
                  // Turning ON: warn if non-CFI items already pending (new LEXP
                  // path). For existing LEXPs the server enforces the same guard.
                  const incompatible = pendingQuestions.some(
                    (q) => q.item_type !== "cfi_mc" && q.item_type !== "likert"
                  );
                  if (incompatible) {
                    toast.error("Remove non-CFI items before enabling CFI mode.");
                    return;
                  }
                }
                setForm((p) => ({ ...p, cfi_mode: !p.cfi_mode }));
              }}
              data-testid="le-cfi-toggle"
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ${
                form.cfi_mode ? "bg-fuchsia-500" : "bg-white/10"
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform duration-200 ${
                  form.cfi_mode ? "translate-x-5" : "translate-x-0"
                }`}
              />
            </button>
          </div>
          {form.cfi_mode && (
            <div className="bg-[#0A0C10] border border-fuchsia-500/20 rounded-lg p-3" data-testid="le-cfi-likert-pinned">
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-md bg-fuchsia-500/20 border border-fuchsia-500/40 flex items-center justify-center text-fuchsia-300 text-xs font-bold">L</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-body font-medium text-white">Confidence Likert (system)</p>
                  <p className="text-xs text-[#6E7787] font-body">1 = Not Confident · 5 = Completely Confident · auto-attached on save</p>
                </div>
                <Badge className="bg-white/5 text-[#A1A9B8] border-white/10 text-xs">Fixed scale</Badge>
              </div>
            </div>
          )}
        </div>

        {/* Learning Content */}
        <div className="bg-[#11141A] border border-white/10 rounded-xl p-4 sm:p-6 space-y-3">
          <Label className="text-[#A1A9B8] font-body text-sm">Learning Content</Label>
          <RichTextEditor
            value={form.patient_scenario}
            onChange={(v) => setForm({ ...form, patient_scenario: v })}
            placeholder="Describe the patient scenario..."
            testId="le-scenario-editor"
          />
        </div>

        {/* Questions */}
        <div className="bg-[#11141A] border border-white/10 rounded-xl p-4 sm:p-6 space-y-3">
          <Label className="text-[#A1A9B8] font-body text-sm">Questions</Label>
          {isEdit ? (
            <ItemAssigner entityType="lexp" entityId={id} cfiMode={!!form.cfi_mode} />
          ) : (
            <div className="space-y-4" data-testid="local-question-picker">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-body font-bold text-base text-white">Assigned Questions</h3>
                  {pendingQuestions.length > 0 && (
                    <p className="text-xs text-[#6E7787] font-body mt-0.5">
                      {pendingQuestions.length} question{pendingQuestions.length !== 1 ? "s" : ""} — {pendingQuestions.reduce((s, q) => s + (q.point_value || 0), 0)} total points
                    </p>
                  )}
                </div>
                <Button type="button" onClick={openQuestionPicker} data-testid="add-question-btn"
                  className="h-9 px-4 font-body text-sm font-medium text-white rounded-lg hover:opacity-90"
                  style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
                >
                  <Plus className="h-4 w-4 mr-1.5" />Add Question
                </Button>
              </div>

              {pendingQuestions.length === 0 ? (
                <div className="bg-[#0A0C10] border border-dashed border-white/10 rounded-xl p-10 text-center" data-testid="questions-empty">
                  <Puzzle className="h-8 w-8 text-[#6E7787] mx-auto mb-2" />
                  <p className="text-sm text-[#6E7787] font-body">No questions assigned yet.</p>
                </div>
              ) : (
                <div className="space-y-2" data-testid="pending-questions-list">
                  {pendingQuestions.map((item, idx) => (
                    <div key={item.id} className="bg-[#11141A] border border-white/10 rounded-lg px-4 py-3 flex items-center gap-3 hover:border-white/20 transition-colors" data-testid={`pending-q-${item.id}`}>
                      <div className="flex flex-col items-center gap-0.5 shrink-0">
                        <button type="button" onClick={() => movePendingQuestion(idx, "up")} disabled={idx === 0} className="text-[#6E7787] hover:text-white disabled:opacity-30 p-0.5">
                          <ChevronUp className="h-4 w-4" />
                        </button>
                        <span className="text-xs text-[#6E7787] font-body w-5 text-center">{idx + 1}</span>
                        <button type="button" onClick={() => movePendingQuestion(idx, "down")} disabled={idx === pendingQuestions.length - 1} className="text-[#6E7787] hover:text-white disabled:opacity-30 p-0.5">
                          <ChevronDown className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-body font-medium text-white truncate">{item.title}</p>
                        <p className="text-xs text-[#6E7787] font-body truncate mt-0.5">{(item.stem || "").replace(/<[^>]*>/g, "").substring(0, 80)}</p>
                      </div>
                      <Badge className={`text-xs font-body shrink-0 ${TYPE_COLORS[item.item_type] || ""}`}>
                        {TYPE_LABELS[item.item_type] || item.item_type}
                      </Badge>
                      <Badge className={`text-xs font-body shrink-0 ${CAT_COLORS[item.item_category] || CAT_COLORS.assessment}`}>
                        {item.item_category === "survey" ? "Survey" : "Assessment"}
                      </Badge>
                      <span className="text-xs text-white font-body font-medium shrink-0">{item.point_value}pt</span>
                      <Button type="button" variant="ghost" size="sm" onClick={() => removePendingQuestion(item.id)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-7 px-2 shrink-0" data-testid={`remove-pending-q-${item.id}`}>
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Question Picker Dialog */}
              <Dialog open={showQuestionPicker} onOpenChange={setShowQuestionPicker}>
                <DialogContent className="bg-[#11141A] border-white/10 text-white max-w-lg max-h-[80vh] flex flex-col" data-testid="question-picker-modal">
                  <DialogHeader>
                    <DialogTitle className="font-body font-bold text-xl text-white">Add Question</DialogTitle>
                    <DialogDescription className="text-[#6E7787] font-body">Search and select a published question to assign.</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-2 pb-2 border-b border-white/10">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#6E7787]" />
                      <Input value={pickerSearch} onChange={(e) => setPickerSearch(e.target.value)} placeholder="Search questions by title, stem, or tag..." data-testid="question-picker-search" className="pl-8 bg-[#0A0C10] border-white/10 text-white placeholder:text-[#6E7787] h-9 text-sm" />
                    </div>
                    <div className="flex gap-1 flex-wrap">
                      {(form.cfi_mode ? ["all", "cfi_mc"] : ["all", "mc", "sata", "task", "likert"]).map((t) => (
                        <button key={t} type="button" onClick={() => setPickerTypeFilter(t)} data-testid={`picker-filter-${t}`}
                          className={`px-3 py-1 text-xs font-body rounded-md border transition-colors ${pickerTypeFilter === t ? "border-white/20 bg-white/10 text-white" : "border-white/10 text-[#6E7787] hover:text-white"}`}
                        >
                          {t === "all" ? "All" : TYPE_LABELS[t]}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="overflow-y-auto flex-1 min-h-0">
                    {pickerLoading ? (
                      <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" style={{ color: "var(--primary-color)" }} /></div>
                    ) : filteredPickerQuestions.length === 0 ? (
                      <p className="text-[#6E7787] font-body text-sm py-6 text-center">
                        {allQuestions.length === 0 ? "No published questions available." : "No questions match your search."}
                      </p>
                    ) : (
                      <div className="space-y-2 mt-2">
                        {filteredPickerQuestions.map((item) => {
                          const added = pendingIds.has(item.id);
                          return (
                            <div key={item.id} className={`border border-white/10 rounded-lg p-3 transition-all ${added ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:border-white/20"}`}
                              onClick={() => !added && addPendingQuestion(item)} data-testid={`picker-q-${item.id}`}
                            >
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <Badge className={`text-xs ${TYPE_COLORS[item.item_type]}`}>{TYPE_LABELS[item.item_type]}</Badge>
                                    <Badge className={`text-xs ${CAT_COLORS[item.item_category] || CAT_COLORS.assessment}`}>{item.item_category === "survey" ? "Survey" : "Assessment"}</Badge>
                                    <h4 className="text-sm font-body font-medium text-white truncate">{item.title}</h4>
                                  </div>
                                  <p className="text-xs text-[#6E7787] font-body mt-0.5 truncate">{(item.stem || "").replace(/<[^>]*>/g, "").substring(0, 60)}</p>
                                </div>
                                {added ? (
                                  <Badge className="bg-white/5 text-[#6E7787] border-white/10 shrink-0">Added</Badge>
                                ) : (
                                  <span className="text-xs text-[#6E7787] font-body shrink-0">{item.point_value}pt</span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          )}
        </div>

        {/* Embedded Content */}
        <div className="bg-[#11141A] border border-white/10 rounded-xl p-4 sm:p-6 space-y-5" data-testid="le-embed-section">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <Globe className="h-4 w-4 text-[#A1A9B8]" />
              <h3 className="font-body text-base font-bold text-white">Embedded Content</h3>
            </div>
            <button
              type="button"
              onClick={() => setForm((p) => ({ ...p, embed_enabled: !p.embed_enabled }))}
              data-testid="le-embed-toggle"
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ${
                form.embed_enabled ? "bg-emerald-500" : "bg-white/10"
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform duration-200 ${
                  form.embed_enabled ? "translate-x-5" : "translate-x-0"
                }`}
              />
            </button>
          </div>
          {form.embed_enabled && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-[#A1A9B8] font-body text-sm">Embed Title</Label>
                <Input
                  value={form.embed_title}
                  onChange={(e) => setForm({ ...form, embed_title: e.target.value })}
                  placeholder="e.g. Watch the procedure walkthrough"
                  data-testid="le-embed-title-input"
                  className="bg-[#0A0C10] border-white/10 text-white placeholder:text-[#6E7787] focus:border-[var(--primary-color,#4A90D9)] h-10"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[#A1A9B8] font-body text-sm">Embed URL <span className="text-red-400">*</span></Label>
                <Input
                  value={form.embed_url}
                  onChange={(e) => setForm({ ...form, embed_url: e.target.value })}
                  placeholder="https://www.youtube.com/embed/..."
                  type="url"
                  data-testid="le-embed-url-input"
                  className="bg-[#0A0C10] border-white/10 text-white placeholder:text-[#6E7787] focus:border-[var(--primary-color,#4A90D9)] h-10"
                />
                <p className="text-xs text-[#6E7787] font-body">Paste the full embed URL (YouTube, Vimeo, or any iframe-compatible URL)</p>
              </div>
              {form.embed_url && (
                <div className="space-y-1.5">
                  <Label className="text-[#A1A9B8] font-body text-sm">Preview</Label>
                  <div className="rounded-lg overflow-hidden border border-white/10">
                    <iframe
                      src={form.embed_url}
                      title={form.embed_title || "Embedded content preview"}
                      className="w-full border-0"
                      style={{ height: "340px" }}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      data-testid="le-embed-preview"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

      </form>
    </div>
  );
}
